import React, { useState, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ArrowRight, Book, Feather, Users, Clock, Coffee, Music, Star, Heart, Lightbulb, Smile, Zap } from 'lucide-react';

export default function Index() {
  const [story, setStory] = useState('');
  const navigate = useNavigate();
  const controls = useAnimation();
  const [ref, inView] = useInView();

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/canvas');
  };

  const reasonsNot = [
    { icon: <Book size={24} />, text: "My life isn't interesting enough", color: "bg-indigo-50" },
    { icon: <Feather size={24} />, text: "I'm not a good writer", color: "bg-pink-50" },
    { icon: <Users size={24} />, text: "Nobody will read my story", color: "bg-blue-50" },
    { icon: <Clock size={24} />, text: "I don't have time to write", color: "bg-green-50" },
    { icon: <Coffee size={24} />, text: "I don't know where to start", color: "bg-yellow-50" },
    { icon: <Music size={24} />, text: "My life isn't exciting enough", color: "bg-purple-50" },
  ];

  const reasonsTo = [
    { icon: <Star size={24} />, text: "Your experiences are unique", color: "bg-yellow-50" },
    { icon: <Heart size={24} />, text: "Becuase it's your story nobody can take it from you", color: "bg-red-50" },
    { icon: <Lightbulb size={24} />, text: "Successful/Failure doesn't matter", color: "bg-green-50" },
    { icon: <Smile size={24} />, text: "From the day you were born and the journey you have lived, no one else has lived the same journey", color: "bg-blue-50" },
    { icon: <Zap size={24} />, text: "No one else have faced those difficulties", color: "bg-purple-50" },
    { icon: <Clock size={24} />, text: "No one else knows knows the story behind that Win", color: "bg-indigo-50" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 p-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-5xl mx-auto text-center mb-16"
      >
        <h1 className="text-5xl font-bold text-gray-900 mb-6">Craft Your Life's Masterpiece</h1>
        <p className="text-xl text-gray-600 mb-8">Every moment, every experience, every triumph and challenge - they all weave together to create your unique story. It's time to share it with the world.</p>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-xl shadow-2xl p-8 mb-12"
        >
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Begin Your Journey</h2>
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input
              type="text"
              placeholder="Once upon a time..."
              value={story}
              onChange={(e) => setStory(e.target.value)}
              className="flex-grow text-lg py-3 border-2 border-gray-300 placeholder-gray-500 text-gray-900"
            />
            <Button type="submit" size="lg" className="bg-[#52A384] text-white font-semibold py-2 px-6 rounded-md flex items-center justify-center">
  <span className="mr-1">Write</span> {/* Reduced margin */}
  <span className="mr-2">Now</span> {/* Added margin for spacing */}
  <ArrowRight className="h-5 w-5" />
</Button>
          </form>
        </motion.div>
      </motion.div>

      <div className="max-w-6xl mx-auto mb-16">
        <h2 className="text-4xl font-bold text-gray-800 text-center mb-10">Common Hesitations</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasonsNot.map((reason, index) => (
            <div
              key={index}
              className={`${reason.color} rounded-xl shadow-md p-6 flex flex-col items-center text-center transition-all duration-300 hover:shadow-lg hover:scale-105`}
            >
              <div className="mb-4 bg-white rounded-full p-3 shadow-sm">
                {React.cloneElement(reason.icon, { className: `text-${reason.color.split('-')[1]}-500` })}
              </div>
              <p className="text-gray-800 font-medium text-lg">{reason.text}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto mb-16">
        <h2 className="text-4xl font-bold text-gray-800 text-center mb-10">Why Your Story Matters</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasonsTo.map((reason, index) => (
            <div
              key={index}
              className={`${reason.color} rounded-xl shadow-md p-6 flex flex-col items-center text-center transition-all duration-300 hover:shadow-lg hover:scale-105`}
            >
              <div className="mb-4 bg-white rounded-full p-3 shadow-sm">
                {React.cloneElement(reason.icon, { className: `text-${reason.color.split('-')[1]}-500` })}
              </div>
              <p className="text-gray-800 font-medium text-lg">{reason.text}</p>
            </div>
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.5 }}
        className="max-w-3xl mx-auto text-center"
      >
        <h2 className="text-4xl font-bold text-gray-800 mb-6">Your Story Awaits</h2>
        <p className="text-xl text-gray-600 mb-8">
          Don't let your unique experiences fade away. Capture the essence of your journey, inspire others, and create a lasting legacy.
        </p>
        <Button 
          size="lg" 
          onClick={() => navigate('/canvas')}
          className="bg-gradient-to-r from-teal-400 to-blue-500 hover:from-teal-500 hover:to-blue-600 text-white font-semibold py-3 px-8 rounded-md transition duration-300 text-lg"
        >
          Start Writing Your LifeStory
        </Button>
      </motion.div>
    </div>
  );
}